// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx
import { CaretDown } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { type AskV2RailPanelSectionId } from "./types";

// why: closed header is 32px tall — 16px radius is a lozenge, 12px is not
export const ASK_V2_RAIL_SECTION_CLASS =
  "flex min-w-0 shrink-0 flex-col overflow-hidden rounded-2xl border border-puntt-neutral-gray-5 bg-base-white";

export const ASK_V2_RAIL_TOGGLE_CLASS =
  "flex h-8 w-full shrink-0 items-center gap-2 bg-base-white px-4 py-2 text-left";

export const ASK_V2_RAIL_TOGGLE_TITLE_CLASS =
  "min-w-0 flex-1 text-puntt-neutral-grayA-11";

// why: same bottom fade as the review comment list, tinted to this card's white fill
export const ASK_V2_RAIL_SCROLL_FADE_CLASS =
  "pointer-events-none absolute inset-x-0 bottom-0 z-10 h-5 bg-[linear-gradient(to_top,rgb(var(--base-white))_15%,transparent)]";

type AskV2RailSectionProps = {
  id: AskV2RailPanelSectionId;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /**
   * The row this section's body should keep in view, by `data-testid`.
   *
   * The body is its own scroller (`max-h-[min(50vh,420px)]`), so a list longer
   * than the fold hides its newest rows below it: on the Progress section that
   * is "each check ticks off as it finishes" with the tick out of sight, which
   * is the same defect as a collapsed section one fold further down. Only the
   * BODY is scrolled — `scrollIntoView` would drag the rail and the page with
   * it — and only when the row is not already in view, so a reader who scrolled
   * back to an earlier row is not yanked down on every frame.
   */
  followTestId?: string;
  /**
   * What the section still says while it is CLOSED.
   *
   * A collapsed section draws no row at all, and a row nobody can see does not
   * tick off for anybody: the Progress section stays readable when closed by
   * naming the step a reader is waiting on in its own header. Omitted (or
   * nothing to say) leaves the collapsed header exactly as it was.
   */
  collapsedSummary?: ReactNode;
};

export function AskV2RailSection({
  id,
  title,
  open,
  onOpenChange,
  children,
  followTestId,
  collapsedSummary,
}: AskV2RailSectionProps) {
  const { t } = useTranslation("ui");
  const bodyRef = useRef<HTMLDivElement>(null);
  const [canScrollMore, setCanScrollMore] = useState(false);

  // Keep the followed row inside the body's own fold. Keyed on the testid, so
  // it runs when the LIVE row advances and not on every frame.
  useEffect(() => {
    const body = bodyRef.current;
    if (!open || !body || !followTestId) return;
    const target = body.querySelector<HTMLElement>(
      `[data-testid="${followTestId}"]`,
    );
    if (!target) return;
    const bodyRect = body.getBoundingClientRect();
    const rowRect = target.getBoundingClientRect();
    if (rowRect.top >= bodyRect.top && rowRect.bottom <= bodyRect.bottom) {
      return;
    }
    body.scrollTop +=
      rowRect.top - bodyRect.top - (body.clientHeight - rowRect.height) / 2;
  }, [open, followTestId]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!open || !el) return;
    const measure = () => {
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      setCanScrollMore(remaining > 1);
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const mutation = new MutationObserver(measure);
    mutation.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    const resize =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(measure);
    resize?.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      mutation.disconnect();
      resize?.disconnect();
    };
  }, [open]);

  return (
    <section
      className={ASK_V2_RAIL_SECTION_CLASS}
      aria-label={title}
      data-testid={`ask-v2-rail-section-${id}`}
      data-panel-expanded={open ? "true" : "false"}
    >
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        aria-label={t("askV2.rail.toggleSection", { section: title })}
        data-testid={`ask-v2-rail-toggle-${id}`}
        className={ASK_V2_RAIL_TOGGLE_CLASS}
      >
        <Text
          as="span"
          size="1"
          weight="medium"
          className={ASK_V2_RAIL_TOGGLE_TITLE_CLASS}
        >
          {title}
        </Text>
        {!open && collapsedSummary ? (
          <span
            className="flex min-w-0 shrink items-center gap-1.5"
            data-testid={`ask-v2-rail-collapsed-${id}`}
          >
            {collapsedSummary}
          </span>
        ) : null}
        <CaretDown
          size={14}
          aria-hidden="true"
          className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${open ? "" : "-rotate-90"}`}
        />
      </button>
      {open ? (
        <div className="relative">
          <div
            ref={bodyRef}
            className="max-h-[min(50vh,420px)] min-w-0 overflow-y-auto overscroll-contain border-t border-puntt-neutral-gray-5 px-4 pb-4 pt-3 [scrollbar-width:thin]"
            data-testid={`ask-v2-rail-body-${id}`}
          >
            {children}
          </div>
          {canScrollMore ? (
            <div
              aria-hidden="true"
              className={ASK_V2_RAIL_SCROLL_FADE_CLASS}
              data-testid={`ask-v2-rail-body-${id}-fade`}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
